import get from "lodash/get"
import React from "react"

type MassageFn = (results: any) => any

// Note: Algolia is currently used only for Library search/filter functionality.
// Refactoring to this component, globals.d.ts typings, and potentially useAlgoliaSearch consumers
// would be required to extend its use to other data sources.
interface RunSearchArgs {
  indices: string[]
  query: string | null | undefined
  authorFacetValues?: string[]
  categoryFacetValues?: string[]
  massageResults?: MassageFn
  resetViewingAll?: boolean
}

interface AlgoliaContextValue {
  results: any | null
  loading: boolean
  error: Error | null
  algoliaEnabled: boolean
  runSearch: (args: RunSearchArgs) => Promise<void>
  viewAll: () => Promise<void>
  hasMore: boolean
  viewingAll: boolean
}

const AlgoliaContext = React.createContext<AlgoliaContextValue | null>(null)

export const AlgoliaProvider: React.FC<React.PropsWithChildren<{}>> = ({
  children
}) => {
  const [results, setResults] = React.useState<any | null>(null)
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<Error | null>(null)
  const [viewingAll, setViewingAll] = React.useState(false)
  const [hasMore, setHasMore] = React.useState(false) // whether there are more results to fetch

  const searchKeys = get(window.bootstrappedData, "integrations.algolia")
  const algoliaEnabled =
    !!window.algoliasearch && !!searchKeys && typeof searchKeys === "object"

  // Shared client instance
  const clientRef = React.useRef<any>(null)
  React.useEffect(() => {
    if (algoliaEnabled && !clientRef.current && window.algoliasearch) {
      const { project_id, search_key } =
        window.bootstrappedData!.integrations!.algolia!

      clientRef.current = window.algoliasearch(project_id, search_key)
    }
  }, [algoliaEnabled])

  const runSearch = React.useCallback(
    async ({
      indices,
      query,
      authorFacetValues,
      categoryFacetValues,
      massageResults,
      resetViewingAll
    }: RunSearchArgs) => {
      if (!algoliaEnabled || !clientRef.current) return
      // Clear results only when there is no text query AND no facet filters.
      // Algolia results are only needed when there is some kind of filter
      // Otherwise, we assume that the consumer will get its data from GQL
      const hasQuery = !!query && String(query).trim().length > 0
      const hasAuthorFacets = !!(authorFacetValues && authorFacetValues.length)
      const hasCategoryFacets = !!(
        categoryFacetValues && categoryFacetValues.length
      )
      const hasAnyFilter = hasQuery || hasAuthorFacets || hasCategoryFacets
      if (!hasAnyFilter) {
        setResults(null)
        return
      }

      if (resetViewingAll) {
        setViewingAll(false)
      }

      const authorsFacetFilters = (authorFacetValues || []).map(
        (v) => `authors.display_name:${v}`
      )
      const categoriesFacetFilters = (categoryFacetValues || []).map(
        (v) => `categories.name:${v}`
      )
      const facetFilters =
        [...authorsFacetFilters, ...categoriesFacetFilters].join(",") || null

      // build queries
      const queries = indices.reduce<any[]>((acc, index) => {
        const entry = searchKeys[index as keyof typeof searchKeys]
        if (!entry) return acc
        const { params } = entry
        if (params) {
          delete params["restrictIndices"]
          params.facets = "*"
          params.facetFilters = facetFilters
          params.hitsPerPage = !resetViewingAll && viewingAll ? 1000 : 25
        }
        const indexName = `${searchKeys.index_prefix}${index}`
        return [
          ...acc,
          {
            indexName,
            query: query || "",
            params
          }
        ]
      }, [])

      if (!queries.length) {
        setResults(null)
        return
      }

      setLoading(true)
      setError(null)

      try {
        const { results: algoliaResults } = await new Promise<any>(
          (resolve, reject) => {
            return clientRef.current.search(queries, (err: any, res: any) =>
              err ? reject(err) : resolve(res)
            )
          }
        )

        const nbHits = algoliaResults?.[0]?.nbHits ?? 0
        setHasMore(!viewingAll && nbHits > 25)

        const finalResults = massageResults
          ? massageResults(algoliaResults)
          : algoliaResults

        // Sometimes finalResults might be defined but only have empty arrays
        const resultsAreEmpty =
          !finalResults ||
          (typeof finalResults === "object" &&
            Object.values(finalResults).every(
              (value: any) => !value || !value.length
            ))

        setResults(resultsAreEmpty ? null : finalResults)
      } catch (e: any) {
        setError(e)
      } finally {
        setLoading(false)
      }
    },
    [algoliaEnabled, searchKeys, viewingAll]
  )

  const viewAll = React.useCallback(async () => {
    if (!hasMore) return
    setViewingAll(true)
  }, [hasMore])

  const value = React.useMemo<AlgoliaContextValue>(
    () => ({
      results,
      loading,
      error,
      algoliaEnabled,
      runSearch,
      viewAll,
      viewingAll,
      hasMore
    }),
    [
      results,
      loading,
      error,
      algoliaEnabled,
      runSearch,
      viewAll,
      viewingAll,
      hasMore
    ]
  )

  return (
    <AlgoliaContext.Provider value={value}>{children}</AlgoliaContext.Provider>
  )
}

// Consumer hook that mirrors the old signature but delegates to the provider
export const useAlgoliaSearch = ({
  query,
  indices = [],
  authorFacetValues,
  categoryFacetValues,
  massageResults
}: {
  query: string | null | undefined
  indices?: string[]
  authorFacetValues?: string[]
  categoryFacetValues?: string[]
  massageResults?: MassageFn
}) => {
  const ctx = React.useContext(AlgoliaContext)
  if (!ctx) {
    return {
      results: null,
      loading: false,
      error: null,
      algoliaEnabled: false,
      viewAll: () => Promise.resolve(),
      viewingAll: false,
      hasMore: false
    }
  }

  const {
    results,
    loading,
    error,
    algoliaEnabled,
    runSearch,
    viewAll,
    viewingAll,
    hasMore
  } = ctx

  // Track previous search params to detect when they change (not viewingAll)
  const prevSearchParamsRef = React.useRef<string>("")
  const currentSearchParams = JSON.stringify({
    query,
    authorFacetValues,
    categoryFacetValues
  })

  React.useEffect(() => {
    const searchParamsChanged =
      prevSearchParamsRef.current !== currentSearchParams

    if (searchParamsChanged) {
      prevSearchParamsRef.current = currentSearchParams
      // Reset viewingAll when search params change
      runSearch({
        indices,
        query,
        authorFacetValues,
        categoryFacetValues,
        massageResults,
        resetViewingAll: true
      })
    } else {
      // Only viewingAll changed, don't reset it
      runSearch({
        indices,
        query,
        authorFacetValues,
        categoryFacetValues,
        massageResults,
        resetViewingAll: false
      })
    }
  }, [runSearch, indices, massageResults, viewingAll, currentSearchParams])

  return {
    results,
    loading,
    error,
    algoliaEnabled,
    viewAll,
    viewingAll,
    hasMore
  }
}
